import { Link } from "react-router-dom";
import { ArrowLeft, PartyPopper, Sparkles, Users, Cake } from "lucide-react";
import { ASSETS } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import BirthdayRSVPForm from "@/components/site/BirthdayRSVPForm";
import Footer from "@/components/site/Footer";
import PWABadge from "@/components/site/PWABadge";

const PERKS = [
  { icon: Users, title: "Big Tables, Bigger Vibes", body: "Long tables that seat 10, 20, or the whole crew. Say the word and we open up the lounge." },
  { icon: Cake, title: "Cake, Candles, All Yours", body: "Bring your own cake — we'll cut, plate, and light it. No corkage nonsense." },
  { icon: Sparkles, title: "Fam Set Menu", body: "Pre-order family style so nobody's waiting on a ticket. Just eat, laugh, take pictures." },
  { icon: PartyPopper, title: "Playlist Requests", body: "Send us your birthday playlist and we'll spin it through the room all night." },
];

export default function Birthdays() {
  const { phone } = useSettings();

  return (
    <div className="min-h-screen bg-[var(--trio-bg)]">
      <header
        className="sticky top-0 z-40 bg-[var(--trio-bg)]/95 backdrop-blur border-b border-[var(--trio-border)]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="birthdays-back-home"
            className="flex items-center gap-2 text-sm sm:text-xs uppercase tracking-widest hover:text-[var(--trio-primary)] py-2 -my-2"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <Link to="/">
            <img src={ASSETS.logo} alt="Trio" className="h-9 w-auto" />
          </Link>
          <div className="w-16" />
        </div>
      </header>

      <section
        data-testid="birthdays-hero"
        className="relative overflow-hidden bg-[var(--trio-text)] text-[var(--trio-bg)]"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://customer-assets.emergentagent.com/job_trio-table/artifacts/1t43menl_Attachment%202.JPG)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(2px)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--trio-text)]/40 via-[var(--trio-text)]/70 to-[var(--trio-text)]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-6">
              Birthdays · Groups · Grown Folk Vibes
            </div>
            <h1 className="font-display uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.85] tracking-tight mb-8">
              Throw your <span className="text-[var(--trio-primary)]">birthday</span> at Trio.
            </h1>
            <p className="text-lg sm:text-xl text-[var(--trio-bg)]/80 max-w-xl leading-relaxed mb-8">
              Soul food, a real room, a real crowd, and a night your people won't forget.
              Fill out the form and we'll call to lock in your date.
            </p>
            {phone && (
              <a
                href={`tel:+1${phone.replace(/\D/g, "")}`}
                data-testid="birthdays-call"
                className="inline-flex items-center gap-2 border border-[var(--trio-bg)]/40 hover:border-[var(--trio-accent)] hover:text-[var(--trio-accent)] px-5 py-3 text-xs tracking-widest uppercase font-bold transition-colors"
              >
                Or Call · {phone}
              </a>
            )}
          </div>

          <div
            data-testid="birthdays-form-wrap"
            className="lg:col-span-5 bg-[var(--trio-bg)] text-[var(--trio-text)] p-6 sm:p-8 shadow-2xl border border-[var(--trio-primary)]/30"
          >
            <BirthdayRSVPForm />
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-b border-[var(--trio-border)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-6">
              <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-4">
                What You Get
              </div>
              <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-[0.9]">
                No stress. <br /> Just <span className="text-[var(--trio-primary)]">celebrate.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 self-end">
              <p className="text-lg text-[var(--trio-muted)] leading-relaxed">
                Every birthday party at Trio comes with the essentials handled — so
                you can be the guest of honor, not the event coordinator.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PERKS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`birthdays-perk-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-8 hover:border-[var(--trio-primary)] transition-colors"
                >
                  <Icon className="w-8 h-8 text-[var(--trio-primary)] mb-4" />
                  <h3 className="font-display uppercase text-2xl tracking-tight mb-2">
                    {p.title}
                  </h3>
                  <p className="text-[var(--trio-muted)] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <PWABadge />
    </div>
  );
}
